/**
 * Color Picker UI Component
 * Vanilla JavaScript UI implementation with full spectrum support
 */

import { ColorPickerCore } from './colorPickerCore.js';
import { favoritesManager } from './favoritesManager.js';
import { rgbToHex, hsvToRgb } from './colorUtils.js';

/**
 * Color Picker UI class
 */
export class ColorPickerUI {
  constructor(container, options = {}) {
    this.container = container;
    this.options = {
      mode: 'inline', // 'inline' or 'modal'
      format: 'hex', // 'hex', 'rgb', 'rgba' - controlled by caller
      showAlpha: true,
      showFavorites: false, // Hidden by default
      showAdvanced: true,
      showQuickColors: true,
      initialColor: '#ffffff',
      onSelect: null,
      onCancel: null,
      ...options,
    };

    this.core = options.api ? options.api.getCore() : new ColorPickerCore();
    this.elements = {};
    this.isDragging = false;
    this.dragTarget = null;

    // Set format on core from options
    this.core.setFormat(this.options.format);

    this.setupEventListeners();

    if (this.options.initialColor) {
      this.core.setColor(this.options.initialColor, true);
    }
  }

  /**
   * Render the color picker UI
   */
  async render() {
    this.container.innerHTML = this.getHTML();
    this.cacheElements();
    this.setupUIEventListeners();
    this.updateUI();

    if (this.options.mode === 'modal') {
      this.setupModal();
    }
  }

  /**
   * Get the main HTML structure
   */
  getHTML() {
    return `
      ${
        this.options.mode === 'modal'
          ? '<div class="color-picker-backdrop"></div>'
          : ''
      }
      <div class="color-picker ${
        this.options.mode === 'modal'
          ? 'color-picker-modal'
          : 'color-picker-inline'
      }">
        ${this.options.mode === 'modal' ? this.getHeaderHTML() : ''}
        <div class="color-picker-content">
          <div class="color-picker-main">
            <div class="color-picker-spectrum-area">
              ${this.getSpectrumHTML()}
              ${this.getHueSliderHTML()}
              ${this.options.showAlpha ? this.getAlphaSliderHTML() : ''}
            </div>
            <div class="color-picker-colors-area">
              ${this.options.showQuickColors ? this.getQuickColorsHTML() : ''}
              ${this.options.showFavorites ? this.getFavoritesHTML() : ''}
            </div>
          </div>
          ${this.getPreviewHTML()}
          ${this.options.showAdvanced ? this.getAdvancedHTML() : ''}
        </div>
        ${this.options.mode === 'modal' ? this.getActionsHTML() : ''}
      </div>
    `;
  }

  /**
   * Get header HTML for modal mode
   */
  getHeaderHTML() {
    return `
      <div class="color-picker-header">
        <h3 class="color-picker-title">Seleziona Colore</h3>
      </div>
    `;
  }

  /**
   * Get spectrum area HTML
   */
  getSpectrumHTML() {
    return `
      <div class="color-picker-spectrum" data-component="spectrum">
        <div class="spectrum-cursor"></div>
      </div>
    `;
  }

  /**
   * Get hue slider HTML
   */
  getHueSliderHTML() {
    return `
      <div class="color-picker-hue" data-component="hue">
        <div class="hue-cursor"></div>
      </div>
    `;
  }

  /**
   * Get alpha slider HTML
   */
  getAlphaSliderHTML() {
    return `
      <div class="color-picker-alpha" data-component="alpha">
        <div class="alpha-gradient"></div>
        <div class="alpha-cursor"></div>
      </div>
    `;
  }

  /**
   * Get preview and input HTML
   */
  getPreviewHTML() {
    return `
      <div class="color-picker-preview">
        <div class="color-preview">
          <div class="color-fill"></div>
        </div>
        <div class="color-inputs">
          <input type="text" class="color-input color-input-hex" data-format="hex" placeholder="#ffffff">
          <input type="text" class="color-input color-input-rgb" data-format="r" placeholder="255">
          <input type="text" class="color-input color-input-rgb" data-format="g" placeholder="255">
          <input type="text" class="color-input color-input-rgb" data-format="b" placeholder="255">
        </div>
      </div>
    `;
  }

  /**
   * Get quick colors HTML
   */
  getQuickColorsHTML() {
    const classicColors = this.core.getClassicColors();

    const classicColorsHTML = classicColors
      .map(
        (color) =>
          `<div class="quick-color" style="background-color: ${color}" data-color="${color}"></div>`
      )
      .join('');

    return `
      <div class="color-picker-quick-colors">
        <div class="section-title">Colori Rapidi</div>
        <div class="quick-colors-grid classic-colors-grid">
          ${classicColorsHTML}
        </div>
      </div>
    `;
  }

  /**
   * Get favorites HTML
   */
  getFavoritesHTML() {
    return `
      <div class="color-picker-favorites">
        <div class="favorites-header">
          <div class="section-title">Preferiti</div>
          <div class="favorites-actions">
            <button class="action-btn favorite-btn" data-action="toggle-favorite">★</button>
            <button class="action-btn" data-action="clear-favorites">Cancella</button>
          </div>
        </div>
        <div class="favorites-content">
          <div class="favorites-grid"></div>
          <div class="favorites-empty" style="display: none;">
            Clicca ★ per aggiungere colori ai preferiti
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Get advanced options HTML
   */
  getAdvancedHTML() {
    return `
      <div class="color-picker-advanced-toggle">
        <button class="toggle-btn" data-action="toggle-advanced">
          Opzioni Avanzate
          <span class="toggle-icon">▼</span>
        </button>
      </div>
      <div class="color-picker-advanced">
        <div class="advanced-section">
          <div class="section-title">Palette di Colori</div>
          <div class="palette-controls">
            <button class="palette-btn active" data-palette="analogous">Analoghi</button>
            <button class="palette-btn" data-palette="complementary">Complementari</button>
            <button class="palette-btn" data-palette="triadic">Triadici</button>
            <button class="palette-btn" data-palette="monochromatic">Monocromatici</button>
          </div>
          <div class="palette-grid"></div>
        </div>
      </div>
    `;
  }

  /**
   * Get action buttons HTML for modal mode
   */
  getActionsHTML() {
    return `
      <div class="color-picker-actions">
        <button class="action-btn secondary" data-action="cancel">Annulla</button>
        <button class="action-btn primary" data-action="select">Seleziona</button>
      </div>
    `;
  }

  /**
   * Cache DOM elements for faster access
   */
  cacheElements() {
    this.elements = {
      picker: this.container.querySelector('.color-picker'),
      backdrop: this.container.querySelector('.color-picker-backdrop'),
      spectrum: this.container.querySelector('[data-component="spectrum"]'),
      spectrumCursor: this.container.querySelector('.spectrum-cursor'),
      hue: this.container.querySelector('[data-component="hue"]'),
      hueCursor: this.container.querySelector('.hue-cursor'),
      alpha: this.container.querySelector('[data-component="alpha"]'),
      alphaGradient: this.container.querySelector('.alpha-gradient'),
      alphaCursor: this.container.querySelector('.alpha-cursor'),
      colorFill: this.container.querySelector('.color-fill'),
      hexInput: this.container.querySelector('[data-format="hex"]'),
      rInput: this.container.querySelector('[data-format="r"]'),
      gInput: this.container.querySelector('[data-format="g"]'),
      bInput: this.container.querySelector('[data-format="b"]'),
      quickColors: this.container.querySelectorAll('.quick-color'),
      favoritesGrid: this.container.querySelector('.favorites-grid'),
      favoritesEmpty: this.container.querySelector('.favorites-empty'),
      favoriteBtn: this.container.querySelector(
        '[data-action="toggle-favorite"]'
      ),
      advancedToggle: this.container.querySelector(
        '[data-action="toggle-advanced"]'
      ),
      advanced: this.container.querySelector('.color-picker-advanced'),
      paletteGrid: this.container.querySelector('.palette-grid'),
      paletteBtns: this.container.querySelectorAll('.palette-btn'),
    };
  }

  /**
   * Setup core event listeners
   */
  setupEventListeners() {
    this.core.on('colorChanged', (color) => {
      this.updateUI();
      if (this.options.onChange) {
        this.options.onChange(color);
      }
    });

    favoritesManager.addListener(() => {
      this.updateFavorites();
    });
  }

  /**
   * Setup UI event listeners
   */
  setupUIEventListeners() {
    // Spectrum interactions
    this.setupSpectrumListeners();

    // Hue slider interactions
    this.setupHueListeners();

    // Alpha slider interactions
    if (this.options.showAlpha) {
      this.setupAlphaListeners();
    }

    // Input field listeners
    this.setupInputListeners();

    // Quick color listeners
    this.setupQuickColorListeners();

    // Favorites listeners
    if (this.options.showFavorites) {
      this.setupFavoritesListeners();
    }

    // Advanced options listeners
    if (this.options.showAdvanced) {
      this.setupAdvancedListeners();
    }

    // Modal listeners
    if (this.options.mode === 'modal') {
      this.setupModalListeners();
    }
  }

  /**
   * Setup spectrum area listeners
   */
  setupSpectrumListeners() {
    if (!this.elements.spectrum) return;

    const handleSpectrumMove = (e) => {
      const rect = this.elements.spectrum.getBoundingClientRect();
      const x = Math.max(0, Math.min(rect.width, e.clientX - rect.left));
      const y = Math.max(0, Math.min(rect.height, e.clientY - rect.top));

      const s = x / rect.width;
      const v = 1 - y / rect.height;

      const { h } = this.core.getCurrentHsv();
      this.core.setColorFromHsv(h, s, v);
    };

    this.elements.spectrum.addEventListener('mousedown', (e) => {
      this.isDragging = true;
      this.dragTarget = 'spectrum';
      handleSpectrumMove(e);
    });

    document.addEventListener('mousemove', (e) => {
      if (this.isDragging && this.dragTarget === 'spectrum') {
        handleSpectrumMove(e);
      }
    });

    document.addEventListener('mouseup', () => {
      if (this.dragTarget === 'spectrum') {
        this.isDragging = false;
        this.dragTarget = null;
      }
    });
  }

  /**
   * Setup hue slider listeners
   */
  setupHueListeners() {
    if (!this.elements.hue) return;

    const handleHueMove = (e) => {
      const rect = this.elements.hue.getBoundingClientRect();
      const x = Math.max(0, Math.min(rect.width, e.clientX - rect.left));
      const h = (x / rect.width) * 360;

      const { s, v } = this.core.getCurrentHsv();
      this.core.setColorFromHsv(h, s, v);
    };

    this.elements.hue.addEventListener('mousedown', (e) => {
      this.isDragging = true;
      this.dragTarget = 'hue';
      handleHueMove(e);
    });

    document.addEventListener('mousemove', (e) => {
      if (this.isDragging && this.dragTarget === 'hue') {
        handleHueMove(e);
      }
    });

    document.addEventListener('mouseup', () => {
      if (this.dragTarget === 'hue') {
        this.isDragging = false;
        this.dragTarget = null;
      }
    });
  }

  /**
   * Setup alpha slider listeners
   */
  setupAlphaListeners() {
    if (!this.elements.alpha) return;

    const handleAlphaMove = (e) => {
      const rect = this.elements.alpha.getBoundingClientRect();
      const x = Math.max(0, Math.min(rect.width, e.clientX - rect.left));
      const alpha = x / rect.width;

      this.core.setAlpha(alpha);
    };

    this.elements.alpha.addEventListener('mousedown', (e) => {
      this.isDragging = true;
      this.dragTarget = 'alpha';
      handleAlphaMove(e);
    });

    document.addEventListener('mousemove', (e) => {
      if (this.isDragging && this.dragTarget === 'alpha') {
        handleAlphaMove(e);
      }
    });

    document.addEventListener('mouseup', () => {
      if (this.dragTarget === 'alpha') {
        this.isDragging = false;
        this.dragTarget = null;
      }
    });
  }

  /**
   * Setup input field listeners
   */
  setupInputListeners() {
    if (this.elements.hexInput) {
      this.elements.hexInput.addEventListener('input', (e) => {
        const value = e.target.value;
        if (value.match(/^#?[0-9a-fA-F]{6}$/)) {
          this.core.setColor(value.startsWith('#') ? value : '#' + value);
        }
      });
    }

    [this.elements.rInput, this.elements.gInput, this.elements.bInput].forEach(
      (input) => {
        if (input) {
          input.addEventListener('input', () => {
            const r = parseInt(this.elements.rInput.value) || 0;
            const g = parseInt(this.elements.gInput.value) || 0;
            const b = parseInt(this.elements.bInput.value) || 0;

            if (
              r >= 0 &&
              r <= 255 &&
              g >= 0 &&
              g <= 255 &&
              b >= 0 &&
              b <= 255
            ) {
              this.core.setColor({ r, g, b, a: this.core.getAlpha() });
            }
          });
        }
      }
    );
  }

  /**
   * Setup quick color listeners
   */
  setupQuickColorListeners() {
    this.elements.quickColors.forEach((element) => {
      element.addEventListener('click', () => {
        const color = element.getAttribute('data-color');
        this.core.setColor(color);
      });
    });
  }

  /**
   * Setup favorites listeners
   */
  setupFavoritesListeners() {
    if (this.elements.favoriteBtn) {
      this.elements.favoriteBtn.addEventListener('click', () => {
        this.core.toggleFavorite();
      });
    }

    const clearBtn = this.container.querySelector(
      '[data-action="clear-favorites"]'
    );
    if (clearBtn) {
      clearBtn.addEventListener('click', () => {
        if (confirm('Cancellare tutti i colori preferiti?')) {
          favoritesManager.clearFavorites();
        }
      });
    }
  }

  /**
   * Setup advanced options listeners
   */
  setupAdvancedListeners() {
    if (this.elements.advancedToggle) {
      this.elements.advancedToggle.addEventListener('click', () => {
        this.toggleAdvanced();
      });
    }

    this.elements.paletteBtns.forEach((btn) => {
      btn.addEventListener('click', () => {
        const paletteType = btn.getAttribute('data-palette');
        this.updatePalette(paletteType);

        // Update active state
        this.elements.paletteBtns.forEach((b) => b.classList.remove('active'));
        btn.classList.add('active');
      });
    });
  }

  /**
   * Setup modal listeners
   */
  setupModalListeners() {
    if (this.elements.backdrop) {
      this.elements.backdrop.addEventListener('click', () => {
        this.cancel();
      });
    }

    const cancelBtn = this.container.querySelector('[data-action="cancel"]');
    const selectBtn = this.container.querySelector('[data-action="select"]');

    if (cancelBtn) {
      cancelBtn.addEventListener('click', () => {
        this.cancel();
      });
    }

    if (selectBtn) {
      selectBtn.addEventListener('click', () => {
        this.select();
      });
    }

    // ESC key handling
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.options.mode === 'modal') {
        this.cancel();
      }
    });
  }

  /**
   * Setup modal backdrop and positioning
   */
  setupModal() {
    // Focus management
    const focusableElements = this.container.querySelectorAll('button, input');
    if (focusableElements.length > 0) {
      focusableElements[0].focus();
    }
  }

  /**
   * Update the entire UI
   */
  updateUI() {
    this.updateSpectrum();
    this.updateHue();
    this.updateAlpha();
    this.updatePreview();
    this.updateInputs();
    this.updateQuickColors();
    this.updateFavorites();
    this.updateFavoriteButton();
  }

  /**
   * Update spectrum cursor position
   */
  updateSpectrum() {
    if (!this.elements.spectrumCursor || !this.elements.spectrum) return;

    const { s, v } = this.core.getCurrentHsv();
    const rect = this.elements.spectrum.getBoundingClientRect();

    const x = s * rect.width;
    const y = (1 - v) * rect.height;

    this.elements.spectrumCursor.style.left = `${x}px`;
    this.elements.spectrumCursor.style.top = `${y}px`;

    // Update spectrum background hue
    const { h } = this.core.getCurrentHsv();
    const hueColor = hsvToRgb(h, 1, 1);
    const hueHex = rgbToHex(hueColor.r, hueColor.g, hueColor.b);

    this.elements.spectrum.style.background = `
      linear-gradient(to right, #fff, transparent),
      linear-gradient(to top, #000, transparent),
      ${hueHex}
    `;
  }

  /**
   * Update hue slider cursor position
   */
  updateHue() {
    if (!this.elements.hueCursor || !this.elements.hue) return;

    const { h } = this.core.getCurrentHsv();
    const rect = this.elements.hue.getBoundingClientRect();
    const x = (h / 360) * rect.width;

    this.elements.hueCursor.style.left = `${x}px`;
  }

  /**
   * Update alpha slider
   */
  updateAlpha() {
    if (
      !this.elements.alpha ||
      !this.elements.alphaGradient ||
      !this.elements.alphaCursor
    )
      return;

    const { r, g, b, a } = this.core.getCurrentColor('object');

    // Update alpha gradient
    this.elements.alphaGradient.style.background = `
      linear-gradient(to right, 
        rgba(${r}, ${g}, ${b}, 0), 
        rgba(${r}, ${g}, ${b}, 1)
      )
    `;

    // Update cursor position
    const rect = this.elements.alpha.getBoundingClientRect();
    const x = a * rect.width;
    this.elements.alphaCursor.style.left = `${x}px`;
  }

  /**
   * Update color preview
   */
  updatePreview() {
    if (!this.elements.colorFill) return;

    const color = this.core.getCurrentColor('rgba');
    this.elements.colorFill.style.backgroundColor = color;
  }

  /**
   * Update input fields
   */
  updateInputs() {
    const { r, g, b } = this.core.getCurrentColor('object');
    const hex = this.core.getCurrentColor('hex');

    if (this.elements.hexInput) {
      this.elements.hexInput.value = hex;
    }

    if (this.elements.rInput) {
      this.elements.rInput.value = r;
    }

    if (this.elements.gInput) {
      this.elements.gInput.value = g;
    }

    if (this.elements.bInput) {
      this.elements.bInput.value = b;
    }
  }

  /**
   * Update quick color selection states
   */
  updateQuickColors() {
    const currentColor = this.core.getCurrentColor('hex');

    this.elements.quickColors.forEach((element) => {
      const elementColor = element.getAttribute('data-color');
      element.classList.toggle('active', elementColor === currentColor);
    });
  }

  /**
   * Update favorites display
   */
  updateFavorites() {
    if (!this.elements.favoritesGrid || !this.elements.favoritesEmpty) return;

    const favorites = favoritesManager.getFavorites();
    const currentColor = this.core.getCurrentColor('hex');

    if (favorites.length === 0) {
      this.elements.favoritesGrid.style.display = 'none';
      this.elements.favoritesEmpty.style.display = 'block';
    } else {
      this.elements.favoritesGrid.style.display = 'grid';
      this.elements.favoritesEmpty.style.display = 'none';

      this.elements.favoritesGrid.innerHTML = favorites
        .map(
          (color) => `
        <div class="favorite-color ${color === currentColor ? 'active' : ''}" 
             style="background-color: ${color}" 
             data-color="${color}">
          <div class="remove-favorite">×</div>
        </div>
      `
        )
        .join('');

      // Add event listeners to favorite colors
      this.elements.favoritesGrid
        .querySelectorAll('.favorite-color')
        .forEach((element) => {
          element.addEventListener('click', (e) => {
            if (!e.target.classList.contains('remove-favorite')) {
              const color = element.getAttribute('data-color');
              this.core.setColor(color);
            }
          });

          const removeBtn = element.querySelector('.remove-favorite');
          removeBtn.addEventListener('click', (e) => {
            e.stopPropagation();
            const color = element.getAttribute('data-color');
            favoritesManager.removeFavorite(color);
          });
        });
    }
  }

  /**
   * Update favorite button state
   */
  updateFavoriteButton() {
    if (!this.elements.favoriteBtn) return;

    const isFavorited = this.core.isCurrentColorFavorited();
    this.elements.favoriteBtn.classList.toggle('is-favorited', isFavorited);
    this.elements.favoriteBtn.textContent = isFavorited ? '★' : '☆';
  }

  /**
   * Toggle advanced options panel
   */
  toggleAdvanced() {
    if (!this.elements.advanced || !this.elements.advancedToggle) return;

    const isExpanded = this.elements.advanced.classList.contains('expanded');

    this.elements.advanced.classList.toggle('expanded');
    this.elements.advancedToggle.classList.toggle('expanded');

    const icon = this.elements.advancedToggle.querySelector('.toggle-icon');
    if (icon) {
      icon.classList.toggle('expanded');
    }

    if (!isExpanded) {
      // Generate initial palette when opening
      this.updatePalette('analogous');
    }
  }

  /**
   * Update color palette display
   */
  updatePalette(type) {
    if (!this.elements.paletteGrid) return;

    const currentColor = this.core.getCurrentColor('hex');
    const palette = this.core.generatePalette(currentColor, type);

    this.elements.paletteGrid.innerHTML = palette
      .map(
        (color) => `
      <div class="palette-color" 
           style="background-color: ${color}" 
           data-color="${color}"></div>
    `
      )
      .join('');

    // Add event listeners
    this.elements.paletteGrid
      .querySelectorAll('.palette-color')
      .forEach((element) => {
        element.addEventListener('click', () => {
          const color = element.getAttribute('data-color');
          this.core.setColor(color);
        });
      });
  }

  /**
   * Handle modal select action
   */
  select() {
    const color = this.core.getCurrentColor();
    if (this.options.onSelect) {
      this.options.onSelect(color);
    }
  }

  /**
   * Handle modal cancel action
   */
  cancel() {
    if (this.options.onCancel) {
      this.options.onCancel();
    }
  }

  /**
   * Get current color
   */
  getCurrentColor(format) {
    return this.core.getCurrentColor(format);
  }

  /**
   * Set color programmatically
   */
  setColor(color) {
    return this.core.setColor(color);
  }

  /**
   * Destroy the color picker
   */
  destroy() {
    // Remove event listeners
    document.removeEventListener('mousemove', this.handleMouseMove);
    document.removeEventListener('mouseup', this.handleMouseUp);

    // Clear container
    if (this.container) {
      this.container.innerHTML = '';
    }
  }
}
